/**
 * ResourceStatusBadge - Status indicator badge
 *
 * Small badge showing resource status with icon and optional label
 */
import React from 'react';
import { CheckCircle, AlertTriangle, AlertCircle, XCircle, HelpCircle } from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
  ok: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    icon: CheckCircle,
    label: 'OK',
  },
  low: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    icon: AlertTriangle,
    label: 'Bas',
  },
  warning: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-200',
    icon: AlertTriangle,
    label: 'Attention',
  },
  critical: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200',
    icon: AlertCircle,
    label: 'Critique',
  },
  replace: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
    icon: XCircle,
    label: 'Remplacer',
  },
  unknown: {
    bg: 'bg-gray-100',
    text: 'text-gray-500',
    border: 'border-gray-200',
    icon: HelpCircle,
    label: 'Inconnu',
  },
};

export function ResourceStatusBadge({
  status = 'unknown',
  showLabel = true,
  showIcon = true,
  size = 'md',
  className = '',
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = config.icon;

  // Size variants
  const sizes = {
    sm: { padding: 'px-1.5 py-0.5', text: 'text-xs', icon: 10, gap: 'gap-0.5' },
    md: { padding: 'px-2 py-1', text: 'text-xs', icon: 12, gap: 'gap-1' },
    lg: { padding: 'px-2.5 py-1', text: 'text-sm', icon: 14, gap: 'gap-1.5' },
  };

  const sizeConfig = sizes[size] || sizes.md;

  return (
    <span
      className={`
        inline-flex items-center ${sizeConfig.gap} ${sizeConfig.padding}
        rounded-full font-medium ${sizeConfig.text}
        ${config.bg} ${config.text} border ${config.border}
        ${className}
      `}
    >
      {showIcon && <Icon size={sizeConfig.icon} />}
      {showLabel && <span>{config.label}</span>}
    </span>
  );
}

// Summary badge for alert bar
export function ResourceAlertSummary({ alertCount, warningCount, criticalCount }) {
  if (alertCount === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
        <CheckCircle size={16} className="text-green-600" />
        <span className="text-sm text-green-700 font-medium">
          Tous les niveaux sont OK
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
      <AlertTriangle size={16} className="text-amber-600" />
      <span className="text-sm text-amber-700 font-medium">
        {criticalCount > 0 && (
          <span className="text-red-700">{criticalCount} critique{criticalCount > 1 ? 's' : ''}</span>
        )}
        {criticalCount > 0 && warningCount > 0 && ', '}
        {warningCount > 0 && (
          <span className="text-orange-700">{warningCount} attention</span>
        )}
        {(criticalCount > 0 || warningCount > 0) && alertCount > criticalCount + warningCount && ', '}
        {alertCount > criticalCount + warningCount && (
          <span className="text-yellow-700">
            {alertCount - criticalCount - warningCount} bas
          </span>
        )}
      </span>
    </div>
  );
}

export default ResourceStatusBadge;
